import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useParams } from 'react-router-dom';
import { Trophy, Gamepad2, Medal, UserPlus, UserMinus, Clock, Check, X } from 'lucide-react';
import { useProfileByPseudo } from '@/features/auth/api';
import { useFriendshipStatus, useSendFriendRequest, useAcceptFriendRequest, useDeclineFriendRequest, useRemoveFriend, } from '@/features/friends/api';
import { MyRecordsCard } from '@/features/leaderboards/MyRecordsCard';
import { ComparisonCard } from '@/features/leaderboards/ComparisonCard';
import { AchievementsShowcase } from '@/features/achievements/AchievementsShowcase';
import { borderColorOf, useEquippedCosmetics } from '@/features/cosmetics/api';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardDescription, CardTitle } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ProgressBar, Skeleton } from '@/components/ui/Loading';
import { useToast } from '@/components/ui/Toast';
import { levelProgress } from '@/lib/xp';
import { formatCompact } from '@/lib/utils';
export function PublicProfilePage() {
    const { pseudo } = useParams();
    const { data: profile, isLoading, isError } = useProfileByPseudo(pseudo);
    if (isLoading) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx(Skeleton, { className: "h-32 w-full" }), _jsx(Skeleton, { className: "h-24 w-full" })] }));
    }
    if (isError || !profile) {
        return (_jsxs(Card, { padding: "lg", className: "mx-auto max-w-md text-center", children: [_jsx(CardTitle, { className: "text-2xl", children: "Profil introuvable" }), _jsxs(CardDescription, { className: "mt-2", children: ["Le pseudo \u00AB ", _jsx("strong", { className: "text-ink-100", children: pseudo }), " \u00BB n'existe pas ou ce profil est priv\u00E9."] })] }));
    }
    const progress = levelProgress(Number(profile.xp), profile.level);
    const winRate = profile.total_games > 0 ? Math.round((profile.total_wins / profile.total_games) * 100) : 0;
    return (_jsx(PublicProfileInner, { profile: profile, progress: progress, winRate: winRate }));
}
function PublicProfileInner({ profile, progress, winRate, }) {
    const { data: equipped } = useEquippedCosmetics(profile.id);
    const borderColor = borderColorOf(equipped?.border);
    const title = equipped?.title;
    return (_jsxs("div", { className: "space-y-6", children: [_jsx(Card, { variant: "glow", padding: "lg", children: _jsxs("div", { className: "flex flex-col gap-6 md:flex-row md:items-center", children: [_jsx(Avatar, { pseudo: profile.pseudo, src: profile.avatar_url, size: "2xl", borderColor: borderColor ?? null, border: borderColor ? 'none' : 'electric' }), _jsxs("div", { className: "flex-1 space-y-3", children: [_jsx("h1", { className: "font-display text-3xl uppercase tracking-tight text-ink-50", children: profile.pseudo }), title && (_jsx("p", { className: "-mt-2 font-display text-sm uppercase tracking-wider text-gradient-flame", children: title.name })), _jsxs("div", { className: "space-y-1.5", children: [_jsxs("div", { className: "flex items-baseline gap-3 text-sm", children: [_jsxs(Badge, { variant: "electric", size: "lg", children: ["Niv. ", progress.level] }), _jsxs("span", { className: "font-mono text-ink-300", children: [progress.xpInLevel, "/", progress.xpForNextLevel, " XP"] }), _jsxs("span", { className: "ml-auto font-mono text-xs text-ink-500", children: [formatCompact(progress.xp), " XP au total"] })] }), _jsx(ProgressBar, { value: progress.percent, variant: "electric" })] }), _jsx(FriendshipActions, { otherId: profile.id, pseudo: profile.pseudo })] })] }) }), _jsxs("div", { className: "grid gap-4 md:grid-cols-3", children: [_jsx(StatCard, { label: "Parties", value: profile.total_games, icon: _jsx(Gamepad2, { className: "h-5 w-5" }) }), _jsx(StatCard, { label: "Victoires", value: profile.total_wins, icon: _jsx(Trophy, { className: "h-5 w-5 text-gold" }) }), _jsx(StatCard, { label: "Win rate", value: `${winRate}%`, icon: _jsx(Medal, { className: "h-5 w-5 text-flame-400" }) })] }), _jsx(AchievementsShowcase, { playerId: profile.id }), _jsx(MyRecordsCard, { playerId: profile.id, title: `Records de ${profile.pseudo}`, emptyMessage: `${profile.pseudo} n'a pas encore de score classé.` }), _jsx(ComparisonCard, { otherId: profile.id, otherPseudo: profile.pseudo, otherAvatar: profile.avatar_url })] }));
}
// ────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon }) {
    return (_jsxs(Card, { padding: "md", className: "flex items-center gap-3", children: [_jsx("span", { className: "rounded-xl bg-ink-800 p-2.5", children: icon }), _jsxs("div", { children: [_jsx("p", { className: "text-xs uppercase tracking-wider text-ink-400", children: label }), _jsx("p", { className: "font-display text-2xl text-ink-50", children: value })] })] }));
}
/**
 * Affiche le bon CTA selon la relation. self → rien, none → Ajouter,
 * pending_outgoing → Annuler la demande, pending_incoming → Accepter/Refuser,
 * accepted → Retirer.
 */
function FriendshipActions({ otherId, pseudo }) {
    const { user } = useAuth();
    const { data: status, isLoading } = useFriendshipStatus(otherId);
    const send = useSendFriendRequest();
    const accept = useAcceptFriendRequest();
    const decline = useDeclineFriendRequest();
    const remove = useRemoveFriend();
    const { toast } = useToast();
    if (!user || isLoading || !status)
        return null;
    if (status === 'self')
        return null;
    if (status === 'none') {
        return (_jsx(Button, { variant: "primary", leftIcon: _jsx(UserPlus, { className: "h-4 w-4" }), isLoading: send.isPending, onClick: async () => {
                try {
                    await send.mutateAsync({ pseudo, otherId });
                    toast({ tone: 'success', title: 'Demande envoyée', description: `${pseudo} doit accepter pour que vous deveniez amis.` });
                }
                catch {
                    toast({ tone: 'error', title: 'Échec', description: "Impossible d'envoyer la demande." });
                }
            }, children: "Ajouter en ami" }));
    }
    if (status === 'pending_outgoing') {
        return (_jsx(Button, { variant: "secondary", leftIcon: _jsx(Clock, { className: "h-4 w-4" }), isLoading: decline.isPending, onClick: async () => {
                try {
                    // L'autre joueur est `addressee` ici. On supprime via decline en
                    // tant que requester courant : utilise remove_friend qui accepte
                    // les deux directions.
                    await remove.mutateAsync(otherId);
                    toast({ title: 'Demande annulée' });
                }
                catch {
                    toast({ tone: 'error', title: 'Échec' });
                }
            }, children: "Demande envoy\u00E9e \u2014 annuler" }));
    }
    if (status === 'pending_incoming') {
        return (_jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx(Button, { variant: "primary", leftIcon: _jsx(Check, { className: "h-4 w-4" }), isLoading: accept.isPending, onClick: async () => {
                        try {
                            await accept.mutateAsync(otherId);
                            toast({ tone: 'success', title: 'Ami ajouté !', description: `${pseudo} et toi êtes désormais amis.` });
                        }
                        catch {
                            toast({ tone: 'error', title: 'Échec' });
                        }
                    }, children: "Accepter" }), _jsx(Button, { variant: "ghost", leftIcon: _jsx(X, { className: "h-4 w-4" }), isLoading: decline.isPending, onClick: async () => {
                        try {
                            await decline.mutateAsync(otherId);
                            toast({ title: 'Demande refusée' });
                        }
                        catch {
                            toast({ tone: 'error', title: 'Échec' });
                        }
                    }, children: "Refuser" })] }));
    }
    if (status === 'accepted') {
        return (_jsx(Button, { variant: "secondary", leftIcon: _jsx(UserMinus, { className: "h-4 w-4" }), isLoading: remove.isPending, onClick: async () => {
                try {
                    await remove.mutateAsync(otherId);
                    toast({ title: 'Amitié retirée' });
                }
                catch {
                    toast({ tone: 'error', title: 'Échec' });
                }
            }, children: "Retirer des amis" }));
    }
    return null;
}
